import React, { useState, useEffect } from "react";
import { C, fmt, statusLabel, text } from "../theme";
import { api } from "../api";
import { usePermissions } from "../PermissionContext";

import { Card, Btn, Notice, StatusPill as Badge, ConfirmDialog, DocLink, rowAction } from "../components/ui";

const cell = { padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${C.gray200}`, color: C.gray600, fontWeight: 600, fontSize: text.xs, textTransform: "uppercase" };

export default function VendorEvaluationPage({ initialId, onOpenDoc }) {
  const { me, can } = usePermissions();
  const [tenders, setTenders] = useState([]);
  const [sel, setSel] = useState(initialId || null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Owned here so a message set by the detail view survives navigating back.
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => {
    setLoading(true);
    Promise.all([api.getVendorEvaluations(), fetch("/api/me/users").then(r => r.json()).catch(() => [])])
      .then(([t, u]) => { setTenders(t); setUsers(u); })
      .catch(e => setNotice({ kind: "error", text: e.message })).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  // A sel the loaded list doesn't contain (deep link outside the user's data
  // scope, or a stale id) falls through to the list view.
  const selT = sel ? tenders.find(x => x.id === sel) : null;
  if (selT) {
    return <TenderDetail tender={selT} users={users} me={me} can={can} onBack={() => setSel(null)} reload={load} notice={notice} notify={setNotice} onOpenDoc={onOpenDoc} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Vendor Evaluation</h1>
        <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>{tenders.length} records · build a vendor evaluation from a posted tender request</p>
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Evaluation No.", "Title", "Date", "Requestor", "Budget Ref", "Total", "Status"].map((h, i) => <th scope="col" key={i} style={cell}>{h}</th>)}</tr></thead>
          <tbody>
            {tenders.map(t => (
              <tr key={t.id} {...rowAction(() => { setNotice(null); setSel(t.id); })} style={{ cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: C.primary }}>{t.evaluationNumber}</td>
                <td style={{ padding: "10px 12px" }}>{t.title}</td>
                <td style={{ padding: "10px 12px" }}>{new Date(t.date).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px" }}>{t.requestor?.displayName}</td>
                <td style={{ padding: "10px 12px", fontSize: text.xs, color: C.primary }}>{t.budgetRef}</td>
                <td style={{ padding: "10px 12px" }}>{fmt(t.total)}</td>
                <td style={{ padding: "10px 12px" }}><Badge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function TenderDetail({ tender, users, me, can, onBack, reload, notice, notify, onOpenDoc }) {
  const t = tender;
  const isDraft = t.status === "DRAFT";
  const [bids, setBids] = useState(t.bids?.length ? t.bids : [{ vendorName: "", price: 0 }]);
  const [criteria, setCriteria] = useState(t.criteria?.length ? t.criteria : [{ name: "Price", weight: 40, isPrice: true }, { name: "Technical", weight: 60, isPrice: false }]);
  const [evalIds, setEvalIds] = useState((t.evaluators || []).map(e => e.user.id));
  const [evalSearch, setEvalSearch] = useState("");
  // Vendor register (UX-17): suggest registered vendors in the bid rows.
  const [vendors, setVendors] = useState([]);
  useEffect(() => { api.getReferenceLookup("vendors").then(v => setVendors(Array.isArray(v) ? v.filter(x => x.isActive !== false) : [])).catch(() => setVendors([])); }, []);
  // Which commitment is awaiting confirmation (UX-12/52).
  const [confirming, setConfirming] = useState(null); // { kind: "submit" } | { kind: "award", row } | { kind: "convert" } | null

  // The parent owns the notice, so messages survive onBack() to the list;
  // on failure the draft setup stays in the form.
  const saveSetup = async () => {
    try {
      await api.updateVendorEvaluation(t.id, { bids, criteria, evaluatorUserIds: evalIds });
      notify({ kind: "success", text: "Setup saved — bids, criteria and evaluators updated." }); reload();
    } catch (e) { notify({ kind: "error", text: e.message }); }
  };
  const submit = async () => {
    setConfirming(null);
    try {
      // Persist the current setup before submitting — the server validates the
      // saved record, so submitting without saving first would validate stale data.
      await api.updateVendorEvaluation(t.id, { bids, criteria, evaluatorUserIds: evalIds });
      const r = await api.submitVendorEvaluation(t.id);
      notify({ kind: "success", text: r.message }); reload(); onBack();
    } catch (e) { notify({ kind: "error", text: e.message }); }
  };
  const award = async (bidId, reason) => {
    setConfirming(null);
    try { const r = await api.awardEvaluation(t.id, { bidId, reason: reason || "" }); notify({ kind: "success", text: r.message }); reload(); } catch (e) { notify({ kind: "error", text: e.message }); }
  };
  const convert = async () => {
    setConfirming(null);
    try {
      const r = await api.convertEvaluationToPO(t.id);
      // Hand off to the created PO instead of leaving the user on the
      // converted evaluation (UX-12/52).
      if (onOpenDoc && r.poId) { onOpenDoc("po", r.poId); return; }
      notify({ kind: "success", text: r.message }); reload();
    } catch (e) { notify({ kind: "error", text: e.message }); }
  };

  const weightSum = criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);
  const qualNames = (t.criteria || []).filter(c => !c.isPrice).map(c => `${c.name} (${c.weight}%)`);
  const priceCrit = (t.criteria || []).find(c => c.isPrice);

  const evalQuery = evalSearch.trim().toLowerCase();
  const shownEvaluators = evalQuery
    ? users.filter(u => u.displayName.toLowerCase().includes(evalQuery))
    : users;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <Btn variant="outline" onClick={onBack}>← Back</Btn>
        <h1 style={{ fontSize: text.h2, fontWeight: 700, margin: 0 }}>{t.evaluationNumber}</h1>
        <Badge status={t.status} />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {isDraft && can("vendorEvaluation", "edit") && <Btn variant="success" onClick={() => setConfirming({ kind: "submit" })}>Submit</Btn>}
          {t.status === "AWARDED" && can("po", "create") && <Btn onClick={() => setConfirming({ kind: "convert" })}>Create PO</Btn>}
        </div>
      </div>

      <ConfirmDialog
        open={confirming?.kind === "submit"}
        title="Open this evaluation for scoring?"
        confirmLabel="Open for scoring"
        onConfirm={submit}
        onCancel={() => setConfirming(null)}
      >
        {`The current setup (${bids.length} bid${bids.length === 1 ? "" : "s"}, ${evalIds.length} evaluator${evalIds.length === 1 ? "" : "s"}) is saved and locked, and the assigned evaluators are asked to score. Consolidation happens automatically when the last evaluator submits.`}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming?.kind === "award"}
        title={confirming?.row?.recommended ? "Award to the recommended bid?" : "Award against the recommendation?"}
        confirmLabel="Award"
        variant={confirming?.row?.recommended ? "success" : "danger"}
        requireReason={!!confirming?.row && !confirming.row.recommended}
        reasonLabel="Reason for overriding the recommended bid"
        onConfirm={(reason) => award(confirming.row.bidId, reason)}
        onCancel={() => setConfirming(null)}
      >
        {confirming?.row ? `This awards the evaluation to ${confirming.row.vendorName} at ${fmt(confirming.row.price)}. The award is recorded with the consolidated ranking and enables PO creation at this price.` : ""}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming?.kind === "convert"}
        title="Create the purchase order?"
        confirmLabel="Create PO"
        onConfirm={convert}
        onCancel={() => setConfirming(null)}
      >
        {`A draft PO is created for ${t.awardedBid?.vendorName || "the awarded vendor"} at the awarded ${fmt(t.awardedBid?.price)} (one line, GST added at the default rate), and this evaluation becomes Converted. You'll be taken to the new PO.`}
      </ConfirmDialog>

      <Notice notice={notice} onDismiss={() => notify(null)} />

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, fontSize: text.md }}>
          <div><span style={{ color: C.gray500 }}>Title:</span> <strong>{t.title}</strong></div>
          <div><span style={{ color: C.gray500 }}>Requestor:</span> <strong>{t.requestor?.displayName}</strong></div>
          <div><span style={{ color: C.gray500 }}>From AOR:</span> {onOpenDoc && t.aor?.id ? <DocLink onClick={() => onOpenDoc("aor", t.aor.id)}>{t.aor.aorNumber}</DocLink> : <strong>{t.aor?.aorNumber}</strong>}</div>
          {t.tenderRequest && (
            <div><span style={{ color: C.gray500 }}>From tender:</span> {onOpenDoc ? <DocLink onClick={() => onOpenDoc("tender-requests", t.tenderRequest.id)}>{t.tenderRequest.itqNumber || t.tenderRequest.requestNumber}</DocLink> : <strong>{t.tenderRequest.itqNumber || t.tenderRequest.requestNumber}</strong>}</div>
          )}
          <div><span style={{ color: C.gray500 }}>Budget Ref:</span> <strong style={{ color: C.primary }}>{t.budgetRef}</strong></div>
          <div><span style={{ color: C.gray500 }}>Budget Total:</span> <strong>{fmt(t.total)}</strong></div>
          <div><span style={{ color: C.gray500 }}>Closing:</span> <strong>{t.closingDate ? new Date(t.closingDate).toLocaleDateString() : "—"}</strong></div>
          {t.awardedBid && <div><span style={{ color: C.gray500 }}>Awarded to:</span> <strong style={{ color: C.success }}>{t.awardedBid.vendorName} ({fmt(t.awardedBid.price)})</strong></div>}
          {t.po && (
            <div><span style={{ color: C.gray500 }}>Purchase order:</span> {onOpenDoc ? <DocLink onClick={() => onOpenDoc("po", t.po.id)}>{t.po.poNumber} ({statusLabel(t.po.status)})</DocLink> : <strong>{t.po.poNumber} ({statusLabel(t.po.status)})</strong>}</div>
          )}
        </div>
      </Card>

      {/* Bids */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Bids</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Vendor", "Price", isDraft ? "" : null].filter(x => x !== null).map((h, i) => <th scope="col" key={i} style={cell}>{h}</th>)}</tr></thead>
          <tbody>
            {(isDraft ? bids : t.bids).map((b, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "8px 12px" }}>
                  {isDraft
                    ? <input aria-label={`Bid ${i + 1} vendor name`} list="ve-vendor-register" value={b.vendorName} onChange={e => setBids(bids.map((x, j) => j === i ? { ...x, vendorName: e.target.value } : x))} style={{ width: "100%", padding: 6, border: `1px solid ${C.gray300}`, borderRadius: 4 }} />
                    : b.vendorName}
                </td>
                <td style={{ padding: "8px 12px", width: 160 }}>
                  {isDraft
                    ? <input type="number" aria-label={`Bid ${i + 1} price`} value={b.price} onChange={e => setBids(bids.map((x, j) => j === i ? { ...x, price: Number(e.target.value) } : x))} style={{ width: "100%", padding: 6, border: `1px solid ${C.gray300}`, borderRadius: 4 }} />
                    : fmt(b.price)}
                </td>
                {isDraft && <td style={{ width: 40 }}>{bids.length > 1 && <button type="button" onClick={() => setBids(bids.filter((_, j) => j !== i))} aria-label={`Remove bid ${i + 1}`} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: text.h3 }}>×</button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {isDraft && (
          <>
            {/* Registered vendors (admin Reference Data) as suggestions (UX-17). */}
            <datalist id="ve-vendor-register">
              {vendors.map(v => <option key={v.id} value={v.label}>{v.code}</option>)}
            </datalist>
            <Btn variant="outline" onClick={() => setBids([...bids, { vendorName: "", price: 0 }])}>+ Add bid</Btn>
            {vendors.length > 0 && <span style={{ marginLeft: 10, fontSize: text.sm, color: C.gray500 }}>Vendor names suggest from the vendor register ({vendors.map(v => v.code).join(", ")}); free text is allowed for new vendors.</span>}
          </>
        )}
      </Card>

      {/* Criteria */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Criteria &amp; weights {isDraft && <span style={{ color: weightSum === 100 ? C.success : C.danger, fontWeight: 600 }}>(sum {weightSum}%)</span>}</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
          <thead><tr>{["Criterion", "Weight %", "Price (auto)", isDraft ? "" : null].filter(x => x !== null).map((h, i) => <th scope="col" key={i} style={cell}>{h}</th>)}</tr></thead>
          <tbody>
            {(isDraft ? criteria : t.criteria).map((c, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.gray100}` }}>
                <td style={{ padding: "8px 12px" }}>{isDraft ? <input aria-label={`Criterion ${i + 1} name`} value={c.name} onChange={e => setCriteria(criteria.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} style={{ width: "100%", padding: 6, border: `1px solid ${C.gray300}`, borderRadius: 4 }} /> : c.name}</td>
                <td style={{ padding: "8px 12px", width: 120 }}>{isDraft ? <input type="number" aria-label={`Criterion ${i + 1} weight`} value={c.weight} onChange={e => setCriteria(criteria.map((x, j) => j === i ? { ...x, weight: Number(e.target.value) } : x))} style={{ width: "100%", padding: 6, border: `1px solid ${C.gray300}`, borderRadius: 4 }} /> : c.weight + "%"}</td>
                <td style={{ padding: "8px 12px", width: 120 }}>{isDraft ? <input type="checkbox" aria-label={`${c.name || `Criterion ${i + 1}`} is the price criterion`} checked={!!c.isPrice} onChange={e => setCriteria(criteria.map((x, j) => ({ ...x, isPrice: j === i ? e.target.checked : false })))} /> : (c.isPrice ? "✓" : "—")}</td>
                {isDraft && <td style={{ width: 40 }}>{criteria.length > 1 && <button type="button" onClick={() => setCriteria(criteria.filter((_, j) => j !== i))} aria-label={`Remove criterion ${i + 1}`} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: text.h3 }}>×</button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {isDraft && <Btn variant="outline" onClick={() => setCriteria([...criteria, { name: "", weight: 0, isPrice: false }])}>+ Add criterion</Btn>}
      </Card>

      {/* Evaluators */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>
          Evaluators {!isDraft && t.evaluatorProgress && <span style={{ color: C.gray500, fontWeight: 600 }}>· {t.evaluatorProgress.submitted} of {t.evaluatorProgress.total} submitted</span>}
        </div>
        {isDraft
          ? <div>
              {/* Picker guidance (UX-32): the flat user list gives no hint who
                  should evaluate — say what a sensible panel looks like. */}
              <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 8, lineHeight: 1.5 }}>
                Pick people who can judge the requirement — typically the requesting team and a technical assessor.
                Approvers of the downstream PO and payment-run roles (Preparer/Approver/Executor) are usually <strong>not</strong> evaluators. Each evaluator scores every bid 0–100 per criterion.
              </div>
              <input type="text" aria-label="Search evaluators" value={evalSearch} onChange={e => setEvalSearch(e.target.value)} placeholder="Search evaluators…" style={{ width: "100%", padding: 8, marginBottom: 8, border: `1px solid ${C.gray300}`, borderRadius: 6, fontSize: text.md, boxSizing: "border-box" }} />
              <div style={{ border: `1px solid ${C.gray300}`, borderRadius: 6, maxHeight: 220, overflowY: "auto" }}>
                {shownEvaluators.map(u => {
                  const checked = evalIds.includes(u.id);
                  return (
                    <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", fontSize: text.md, cursor: "pointer", borderBottom: `1px solid ${C.gray100}` }}>
                      <input type="checkbox" checked={checked} onChange={e => setEvalIds(e.target.checked ? [...evalIds, u.id] : evalIds.filter(id => id !== u.id))} />
                      <span>{u.displayName} {u.roleName ? <span style={{ color: C.gray500 }}>({u.roleName})</span> : null}</span>
                    </label>
                  );
                })}
                {shownEvaluators.length === 0 && <div style={{ padding: "10px", fontSize: text.md, color: C.gray500 }}>{users.length === 0 ? "No users available." : "No matching users."}</div>}
              </div>
            </div>
          : (t.evaluators || []).map(e => <div key={e.id} style={{ padding: "6px 0", fontSize: text.md, borderBottom: `1px solid ${C.gray100}` }}>{e.user.displayName} — {e.submittedAt ? <span style={{ color: C.success }}>submitted</span> : <span style={{ color: C.warning }}>pending</span>}</div>)}
      </Card>

      {isDraft && can("vendorEvaluation", "edit") && (
        <div style={{ marginBottom: 16 }}><Btn onClick={saveSetup}>Save setup</Btn></div>
      )}

      {/* Consolidation results */}
      {(t.status === "CONSOLIDATED" || t.status === "AWARDED" || t.status === "CONVERTED") && t.ranking?.length > 0 && (
        <Card>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 10 }}>Consolidated ranking</div>
          {/* Raw vs weighted labeled explicitly (UX-54); the quality column names
              its source criteria instead of inventing "QUALITY SCORE" (UX-29). */}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.md }}>
            <thead><tr>{["Rank", "Vendor", "Price", "Price score (0–100)", "Quality — avg of evaluators (0–100)", "Final (weighted)", ""].map((h, i) => <th scope="col" key={i} style={cell}>{h}</th>)}</tr></thead>
            <tbody>
              {t.ranking.map((r, i) => (
                <tr key={r.bidId} style={{ borderBottom: `1px solid ${C.gray100}`, background: r.recommended ? C.successBg : "transparent" }}>
                  <td style={{ padding: "8px 12px" }}>{i + 1}{r.recommended && " ★"}</td>
                  <td style={{ padding: "8px 12px", fontWeight: 600 }}>{r.vendorName}</td>
                  <td style={{ padding: "8px 12px" }}>{fmt(r.price)}</td>
                  <td style={{ padding: "8px 12px" }}>{r.priceScore}</td>
                  <td style={{ padding: "8px 12px" }}>{r.qualRaw != null ? r.qualRaw : r.qualScore}<span style={{ color: C.gray500, fontSize: text.xs }}> (weighted: {r.qualScore})</span></td>
                  <td style={{ padding: "8px 12px", fontWeight: 700 }}>{r.finalScore}</td>
                  <td style={{ padding: "8px 12px" }}>{t.status === "CONSOLIDATED" && can("vendorEvaluation", "award") && <Btn onClick={() => setConfirming({ kind: "award", row: r })}>{r.recommended ? "Award" : "Award (override)"}</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10, fontSize: text.sm, color: C.gray600, lineHeight: 1.5 }}>
            Quality is the average of the evaluators' 0–100 scores on {qualNames.join(" + ") || "the qualitative criteria"}.
            Final = price score × {priceCrit ? priceCrit.weight : 0}% + quality × its weight{qualNames.length > 1 ? "s" : ""}.
          </div>
          {t.awardReason && <div style={{ marginTop: 10, fontSize: text.sm, color: C.gray600 }}><strong>Award note:</strong> {t.awardReason}</div>}
        </Card>
      )}
    </div>
  );
}
